import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import {
    APPLICATIONS_API,
    CHEMICALS_SEED_PATH,
    CHEMICAL_DB_API_HOST,
    JSON_HEADERS,
    MIX_PATH,
    NOT_FOUND_STATUS
} from './constants';
import type { ApplicationMixAttachment, AttachMix, DetachMix, FetchApplicationMix } from './types';

const useApplicationMix = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedDelete, authenticatedGet, authenticatedPost } = useFetch({
        apiHost: CHEMICAL_DB_API_HOST,
        headers: JSON_HEADERS
    });
    const { withRetry } = useRetry();
    const { ATTACH_FAILED, CONNECTION_ERROR, DETACH_FAILED } = useTranslation();

    const applicationMixUri = (applicationId: string) =>
        `${APPLICATIONS_API}/${applicationId}/${MIX_PATH}`;

    const fetchApplicationMix: FetchApplicationMix = useCallback(
        async ({ applicationId }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    async () => {
                        const response = await authenticatedGet<ApplicationMixAttachment>(
                            applicationMixUri(applicationId)
                        );

                        if (response.error?.includes(NOT_FOUND_STATUS)) {
                            return { data: null, error: null };
                        }

                        return response;
                    },
                    CONNECTION_ERROR,
                    () => fetchApplicationMix({ applicationId })
                );
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedGet, withRetry, CONNECTION_ERROR]
    );

    const attachMix: AttachMix = useCallback(
        async ({ applicationId, mixId }) => {
            try {
                setIsLoading(true);

                const seedResult = await withRetry<{ chemicals: unknown[] }>(
                    () =>
                        authenticatedPost<{ chemicals: unknown[] }>(
                            `${APPLICATIONS_API}/${applicationId}/${CHEMICALS_SEED_PATH}`,
                            JSON.stringify({ mixId })
                        ),
                    ATTACH_FAILED,
                    async () => {
                        const retried = await attachMix({ applicationId, mixId });

                        return retried ? { chemicals: [] } : null;
                    }
                );

                if (!seedResult) {
                    return null;
                }

                return fetchApplicationMix({ applicationId });
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedPost, withRetry, ATTACH_FAILED, fetchApplicationMix]
    );

    const detachMix: DetachMix = useCallback(
        async ({ applicationId }) => {
            try {
                setIsLoading(true);

                const result = await withRetry(
                    () => authenticatedDelete(applicationMixUri(applicationId)),
                    DETACH_FAILED,
                    () => detachMix({ applicationId })
                );

                return result !== null;
            } finally {
                setIsLoading(false);
            }
        },

        [authenticatedDelete, withRetry, DETACH_FAILED]
    );

    return {
        attachMix,
        detachMix,
        fetchApplicationMix,
        isLoading
    };
};

export default useApplicationMix;
